/**
 * Thread rendering: persisted messages + the live tail (runtime) grouped into rows — user bubbles (with
 * attachments, queued/applied/failed state), answers / context cards, event notes, and agent turns
 * ("Details ›" run trace with tool chips, the final answer as markdown, cards, copy / 👍 / 👎 / time) —
 * plus the "Mimic is thinking…" / "Working…" / "Analyzing image…" status line.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Check, CheckCheck, ChevronRight, Clock3, Copy, Info, RotateCcw, Square, ThumbsDown, ThumbsUp, TriangleAlert, X } from "lucide-react";
import { Fragment, memo, useMemo, useState, type ReactNode } from "react";
import { sendMessageFeedback, type AnswerItem, type ChatAttachment, type ChatMessage, type ChatThread } from "@/api/chat";
import { errorMessage } from "@/api/client";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { RobotFace } from "../ui/Page";
import { Spinner } from "../ui/Feedback";
import { Tooltip } from "../ui/Overlay";
import { AttachmentCard, isMediaAttachment, MediaGrid, ScriptReviewCard, type AttachmentCtx } from "./Attachments";
import { Markdown, markdownToPlain } from "./Markdown";
import { AnswersCard, QuestionCard, type QuestionStatus } from "./QuestionCard";
import { answerQuestion, discardLocal, retryLocal, type DraftSeg, type LocalUserMsg, type ThreadLive } from "./runtime";
import { ToolChipGroup, type ToolCallView } from "./ToolChips";
import { copyText, obj, str, timeLabel, toolVerb, useMessageFeedback } from "./util";

// ─── Row model ─────────────────────────────────────────────────────────────

type TurnPart =
  | { type: "text"; text: string; key: string }
  | { type: "tool"; id: string; name: string; input?: unknown; key: string }
  | { type: "attachment"; att: ChatAttachment; key: string };

export type Row =
  | { kind: "user"; key: string; messageId?: string; text: string; attachments: ChatAttachment[]; createdAt?: string; state?: LocalUserMsg["state"]; local?: LocalUserMsg; interjection?: boolean }
  | { kind: "answers"; key: string; data: Record<string, unknown>; createdAt?: string }
  | { kind: "context"; key: string; data: Record<string, unknown> }
  | { kind: "event"; key: string; text: string; tone: "neutral" | "danger" | "success" }
  | { kind: "turn"; key: string; parts: TurnPart[]; live: boolean; messageId?: string; createdAt?: string; feedback?: "up" | "down" | null };

/** Server-added blocks that aren't the user's words: composer context and attachment markers for the model. */
const CONTEXT_BLOCK = /^\s*(<context>[\s\S]*<\/context>|\[Attached (image|video):[\s\S]*\])\s*$/;

function textBlocks(m: ChatMessage): string[] {
  return m.content
    .filter((b) => b.type === "text" && typeof (b as { text?: unknown }).text === "string")
    .map((b) => (b as { text: string }).text)
    .filter((t) => !CONTEXT_BLOCK.test(t) && t.trim().length > 0);
}

export interface ToolResultInfo {
  result?: unknown;
  error?: string;
}

/** tool_use_id → result, from persisted tool_result blocks and the live run. */
export function collectToolResults(messages: ChatMessage[], live: Record<string, { result?: unknown; error?: string }>): Map<string, ToolResultInfo> {
  const out = new Map<string, ToolResultInfo>();
  for (const m of messages) {
    for (const b of m.content) {
      if (b.type !== "tool_result") continue;
      const r = b as { tool_use_id?: string; content?: unknown; is_error?: boolean };
      if (!r.tool_use_id) continue;
      let content: unknown = r.content;
      if (Array.isArray(content)) content = content.map((c) => (c && typeof c === "object" && "text" in c ? (c as { text: unknown }).text : c)).join("\n");
      if (typeof content === "string") {
        try {
          content = JSON.parse(content);
        } catch {
          /* keep text */
        }
      }
      out.set(r.tool_use_id, r.is_error ? { error: typeof content === "string" ? content : JSON.stringify(content) } : { result: content });
    }
  }
  for (const [id, v] of Object.entries(live)) if (!out.has(id)) out.set(id, v);
  return out;
}

function cleanEvent(text: string): string {
  return text.replace(/^\[event\]\s*/i, "").replace(/\s*Result:\s*\{[\s\S]*$/, "").trim();
}

/** Persisted messages (up to the live baseline) + live items → rows. */
export function buildRows(persisted: ChatMessage[], live: ThreadLive, thread: ChatThread | null, serverRunning: boolean): Row[] {
  const base = live.baseCount === null ? persisted : persisted.slice(0, live.baseCount);
  const rows: Row[] = [];
  let turn: Extract<Row, { kind: "turn" }> | null = null;
  const endTurn = () => {
    if (turn && turn.parts.length) rows.push(turn);
    turn = null;
  };
  const ensureTurn = (key: string, liveTurn: boolean) => {
    if (!turn) turn = { kind: "turn", key, parts: [], live: liveTurn };
    if (liveTurn) turn.live = true;
    return turn;
  };

  const pushMessage = (m: ChatMessage, liveTurn: boolean) => {
    const meta = m.meta ?? {};
    const atts = m.attachments ?? [];
    if (m.role === "user") {
      for (const a of atts.filter((x) => x.kind === "answers")) {
        endTurn();
        rows.push({ kind: "answers", key: `${m.id}-ans-${a.id ?? ""}`, data: obj(a.data) ?? {}, createdAt: m.createdAt });
      }
      if (meta.context) {
        endTurn();
        const ctx = atts.find((x) => x.kind === "context");
        rows.push({ kind: "context", key: `${m.id}-ctx`, data: obj(ctx?.data) ?? {} });
        return;
      }
      if (meta.stopped) {
        endTurn();
        rows.push({ kind: "event", key: `${m.id}-stop`, text: "You stopped the run.", tone: "neutral" });
      }
      const text = textBlocks(m).join("\n\n");
      const media = atts.filter(isMediaAttachment);
      if (text || media.length) {
        endTurn();
        rows.push({ kind: "user", key: m.id, messageId: m.id, text, attachments: media, createdAt: m.createdAt, interjection: !!meta.interjection, state: meta.interjection ? "applied" : undefined });
      }
      return;
    }
    if (m.role === "system") {
      const text = textBlocks(m).join("\n");
      if (meta.compaction) {
        endTurn();
        rows.push({ kind: "event", key: m.id, text: "Earlier messages were summarized to save context.", tone: "neutral" });
        return;
      }
      if (!text) return;
      endTurn();
      const tone = meta.notice === "error" ? "danger" : meta.decision === "approved" ? "success" : "neutral";
      rows.push({ kind: "event", key: m.id, text: cleanEvent(text), tone });
      return;
    }
    // assistant / tool
    const t = ensureTurn(m.id, liveTurn);
    if (m.role === "assistant") {
      t.messageId = m.id;
      t.createdAt = m.createdAt ?? t.createdAt;
      t.feedback = m.myFeedback ?? null;
      for (const b of m.content) {
        if (b.type === "text") {
          const txt = String((b as { text?: unknown }).text ?? "");
          if (txt.trim() && !CONTEXT_BLOCK.test(txt)) t.parts.push({ type: "text", text: txt, key: `${m.id}-t${t.parts.length}` });
        } else if (b.type === "tool_use") {
          const u = b as { id: string; name: string; input?: unknown };
          if (u.name === "ask_user") continue;
          t.parts.push({ type: "tool", id: u.id, name: String(u.name ?? "").replace(/__/g, "."), input: u.input, key: `${m.id}-u${u.id}` });
        }
      }
    }
    for (const a of atts) {
      if (a.kind === "answers") continue;
      t.parts.push({ type: "attachment", att: a, key: `${m.id}-a${t.parts.length}` });
    }
  };

  for (const m of base) pushMessage(m, false);

  const persistedUserTexts = live.baseCount === null ? persisted.filter((m) => m.role === "user").map((m) => textBlocks(m).join("\n\n").trim()) : [];
  const seen = new Set(base.map((m) => m.id));
  for (const it of live.items) {
    if (it.kind === "message") {
      if (seen.has(it.message.id)) continue;
      seen.add(it.message.id);
      pushMessage(it.message, true);
    } else if (it.kind === "user") {
      const u = it.msg;
      if (live.baseCount === null && u.state !== "failed" && persistedUserTexts.includes(u.text.trim())) continue;
      const answers = u.attachments.find((a) => a.kind === "answers");
      endTurn();
      if (answers) rows.push({ kind: "answers", key: u.localId, data: obj(answers.data) ?? {}, createdAt: u.createdAt });
      else rows.push({ kind: "user", key: u.localId, text: u.text, attachments: u.attachments, createdAt: u.createdAt, state: u.state, local: u, interjection: u.interjection });
    } else {
      const t = ensureTurn(it.id, true);
      for (const seg of it.segs) t.parts.push(segToPart(seg, `${it.id}-${t.parts.length}`));
    }
  }
  endTurn();

  // Queued interjections the server reported (e.g. after a reload mid-run).
  if (serverRunning && thread?.pendingInputs?.length) {
    const known = new Set(live.items.filter((i) => i.kind === "user").map((i) => (i as { msg: LocalUserMsg }).msg.serverId));
    for (const p of thread.pendingInputs) {
      if (known.has(p.id)) continue;
      rows.push({ kind: "user", key: `pending-${p.id}`, text: p.text, attachments: [], createdAt: p.at, state: "queued", interjection: true });
    }
  }
  return rows;
}

function segToPart(seg: DraftSeg, key: string): TurnPart {
  if (seg.kind === "text") return { type: "text", text: seg.text, key };
  if (seg.kind === "tool") return { type: "tool", id: seg.id, name: seg.name, input: seg.input, key };
  return { type: "attachment", att: seg.attachment, key };
}

// ─── List ──────────────────────────────────────────────────────────────────

export interface MessageListProps {
  rows: Row[];
  live: ThreadLive;
  thread: ChatThread | null;
  threadId: string;
  serverRunning: boolean;
  toolResults: Map<string, ToolResultInfo>;
  ctx: AttachmentCtx;
  agentName: string;
  compact?: boolean;
  onRetryError?: () => void;
}

export function MessageList({ rows, live, thread, threadId, serverRunning, toolResults, ctx, agentName, compact, onRetryError }: MessageListProps) {
  const qc = useQueryClient();
  const running = live.streaming || serverRunning;
  const awaiting = (live.streaming ? live.status : thread?.status) === "awaiting_user";
  const lastTurnIndex = rows.map((r) => r.kind).lastIndexOf("turn");
  const lastIsTurn = rows.length > 0 && lastTurnIndex === rows.length - 1;

  const questionStatus = (att: ChatAttachment): QuestionStatus => {
    const d = obj(att.data) ?? {};
    const s = str(d.status);
    if (s === "answered") return "answered";
    if (s === "cancelled") return "cancelled";
    if (s === "skipped") return "closed";
    const pq = live.streaming ? null : (thread?.pendingQuestion as { attachmentId?: string } | null | undefined);
    if (pq?.attachmentId) return pq.attachmentId === att.id ? "open" : "closed";
    if (live.streaming && live.status === "awaiting_user") return "open";
    if (thread && thread.status !== "awaiting_user" && !live.streaming) return "closed";
    return "open";
  };

  const onAnswer = async (att: ChatAttachment, answers: AnswerItem[], summary: Record<string, unknown>, cancelled: boolean) => {
    if (!att.id) return false;
    return answerQuestion(qc, threadId, att.id, { answers, cancelled, summary });
  };

  // Status line under the last row.
  const runningTool = (() => {
    if (!live.streaming) return null;
    const last = rows[rows.length - 1];
    if (!last || last.kind !== "turn") return null;
    const tools = last.parts.filter((p): p is Extract<TurnPart, { type: "tool" }> => p.type === "tool");
    return tools.reverse().find((t) => !toolResults.has(t.id)) ?? null;
  })();
  const lastRow = rows[rows.length - 1];
  const turnHasText = lastIsTurn && lastRow?.kind === "turn" && lastRow.parts.some((p) => p.type === "text");
  let status: string | null = null;
  if (running && !awaiting) {
    if (live.stopping) status = "Stopping…";
    else if (live.streaming && live.phase === "responding") status = null;
    else if (live.statusLabel) status = live.statusLabel;
    else if (runningTool) status = toolVerb(runningTool.name);
    else if (live.streaming && turnHasText) status = "Working…";
    else status = `${agentName} is thinking…`;
  }

  return (
    <div className={cn("flex flex-col", compact ? "gap-5" : "gap-7")}>
      {rows.map((r, i) => {
        switch (r.kind) {
          case "user":
            return <UserRow key={r.key} row={r} threadId={threadId} compact={compact} />;
          case "answers":
            return (
              <div key={r.key} id={`row-${r.key}`} className="flex flex-col items-end gap-1.5">
                {r.createdAt && <span className="pr-1 text-[12px] text-subtle">{timeLabel(r.createdAt)}</span>}
                <AnswersCard data={r.data} />
              </div>
            );
          case "context":
            return <AttachmentCard key={r.key} att={{ kind: "context", data: r.data }} ctx={ctx} />;
          case "event":
            return <EventRow key={r.key} text={r.text} tone={r.tone} />;
          case "turn": {
            const isLast = i === lastTurnIndex;
            const liveTurn = isLast && running && (r.live || serverRunning);
            return (
              <AgentTurn
                key={r.key}
                row={r}
                live={liveTurn}
                streamingText={liveTurn && live.streaming && live.phase === "responding"}
                toolResults={toolResults}
                ctx={ctx}
                questionStatus={questionStatus}
                onAnswer={onAnswer}
                compact={compact}
              />
            );
          }
          default:
            return null;
        }
      })}
      {status && <StatusLine label={status} pill={!!runningTool || /analy[sz]ing/i.test(status)} />}
      {live.notice === "stopped" && !running && (
        <div className="flex items-center gap-2 text-[13px] text-muted">
          <Square className="size-3 fill-current" /> You stopped this reply.
        </div>
      )}
      {live.error && !live.streaming && (
        <div className="flex items-start gap-3 rounded-2xl border border-danger/40 bg-danger-soft/40 px-4 py-3" role="alert">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger" />
          <div className="min-w-0 flex-1 text-[13.5px]">
            <div className="font-medium text-fg">{agentName} hit a problem</div>
            <div className="mt-0.5 text-muted">{live.error}</div>
          </div>
          {onRetryError && (
            <button type="button" onClick={onRetryError} className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-link hover:text-lavender">
              <RotateCcw className="size-3.5" /> Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Rows ──────────────────────────────────────────────────────────────────

function UserRow({ row, threadId, compact }: { row: Extract<Row, { kind: "user" }>; threadId: string; compact?: boolean }) {
  const qc = useQueryClient();
  const failed = row.state === "failed";
  return (
    <div id={`row-${row.messageId ?? row.key}`} className="flex flex-col items-end gap-1.5">
      <div className={cn("w-fit rounded-2xl bg-elevated px-4 py-3", compact ? "max-w-[92%]" : "max-w-[85%]", failed && "ring-1 ring-danger/50", row.state === "queued" && "opacity-80")}>
        {row.attachments.length > 0 && <MediaGrid items={row.attachments} size="sm" className={cn(row.text && "mb-2.5")} />}
        {row.text && <div className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-fg">{row.text}</div>}
      </div>
      <div className="flex items-center gap-2 pr-1 text-[12px] text-subtle">
        {row.state === "sending" && (
          <span className="inline-flex items-center gap-1">
            <Spinner size={11} /> Sending…
          </span>
        )}
        {row.state === "queued" && (
          <Tooltip content="Sent while Mimic was working — it's applied at the next step">
            <span className="inline-flex items-center gap-1 text-amber">
              <Clock3 className="size-3" /> Queued
            </span>
          </Tooltip>
        )}
        {row.state === "applied" && row.interjection && (
          <Tooltip content="Applied mid-run">
            <span className="inline-flex items-center gap-1 text-success">
              <CheckCheck className="size-3.5" /> Applied
            </span>
          </Tooltip>
        )}
        {failed && row.local && (
          <span className="inline-flex items-center gap-2 text-danger">
            <span title={row.local.error}>Not sent{row.local.error ? ` — ${row.local.error}` : ""}</span>
            <button type="button" onClick={() => void retryLocal(qc, threadId, row.local!.localId)} className="inline-flex items-center gap-1 font-medium text-link hover:text-lavender">
              <RotateCcw className="size-3" /> Retry
            </button>
            <button type="button" onClick={() => discardLocal(threadId, row.local!.localId)} aria-label="Discard message" className="text-muted hover:text-fg">
              <X className="size-3.5" />
            </button>
          </span>
        )}
        {row.createdAt && !failed && <span>{timeLabel(row.createdAt)}</span>}
      </div>
    </div>
  );
}

function EventRow({ text, tone }: { text: string; tone: "neutral" | "danger" | "success" }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 160;
  return (
    <div className={cn("mx-auto flex max-w-[92%] items-start gap-2 rounded-xl px-3 py-2 text-[12.5px]", tone === "danger" ? "bg-danger-soft/40 text-danger" : tone === "success" ? "text-success" : "text-muted")}>
      {tone === "danger" ? <TriangleAlert className="mt-px size-3.5 shrink-0" /> : tone === "success" ? <Check className="mt-px size-3.5 shrink-0" /> : <Info className="mt-px size-3.5 shrink-0" />}
      <span className={cn("min-w-0", !open && long && "line-clamp-2")}>{text}</span>
      {long && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="shrink-0 text-link hover:text-lavender">
          {open ? "Less" : "More"}
        </button>
      )}
    </div>
  );
}

export function StatusLine({ label, pill }: { label: string; pill?: boolean }) {
  return (
    <div className="flex items-center gap-3 animate-fade-in" role="status" aria-live="polite">
      <span className="relative grid size-8 shrink-0 place-items-center">
        <RobotFace color="#d9773f" className="size-7 animate-[pulse_2.2s_ease-in-out_infinite]" />
      </span>
      <span className={cn("inline-flex items-center", pill && "h-9 rounded-full border border-line-strong bg-surface/70 px-4")}>
        <span
          className="bg-clip-text text-[15px] text-transparent [animation:var(--animate-shimmer)]"
          style={{ backgroundImage: "linear-gradient(90deg, #8a8a90 0%, #8a8a90 35%, #f4f4f5 50%, #8a8a90 65%, #8a8a90 100%)", backgroundSize: "200% 100%" }}
        >
          {label}
        </span>
      </span>
    </div>
  );
}

const Caret = () => <span className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[2px] animate-pulse rounded-full bg-fg/80 align-baseline" aria-hidden />;

const AgentTurn = memo(function AgentTurn({
  row,
  live,
  streamingText,
  toolResults,
  ctx,
  questionStatus,
  onAnswer,
  compact,
}: {
  row: Extract<Row, { kind: "turn" }>;
  live: boolean;
  streamingText: boolean;
  toolResults: Map<string, ToolResultInfo>;
  ctx: AttachmentCtx;
  questionStatus: (a: ChatAttachment) => QuestionStatus;
  onAnswer: (a: ChatAttachment, answers: AnswerItem[], summary: Record<string, unknown>, cancelled: boolean) => Promise<boolean>;
  compact?: boolean;
}) {
  const texts = row.parts.filter((p): p is Extract<TurnPart, { type: "text" }> => p.type === "text");
  const tools = row.parts.filter((p): p is Extract<TurnPart, { type: "tool" }> => p.type === "tool");
  const atts = row.parts.filter((p): p is Extract<TurnPart, { type: "attachment" }> => p.type === "attachment");
  const finalText = texts[texts.length - 1];
  // Completed turns fold intermediate narration + tool calls into "Details"; live turns show text as it streams.
  const trace = row.parts.filter((p) => p.type === "tool" || (!live && p.type === "text" && p !== finalText));
  const visibleTexts = live ? texts : finalText ? [finalText] : [];
  const toolView = (t: Extract<TurnPart, { type: "tool" }>): ToolCallView => {
    const r = toolResults.get(t.id);
    return { id: t.id, name: t.name, input: t.input, status: r ? (r.error ? "error" : "done") : live ? "running" : "interrupted", result: r?.result, error: r?.error };
  };
  const media = atts.filter((a) => isMediaAttachment(a.att));
  const cards = atts.filter((a) => !isMediaAttachment(a.att));
  // Video shot plans waiting for review (creatives.plan_video / revise_script results) → "Review script" card.
  const scriptReviews = useMemo(() => {
    const byCreative = new Map<string, ChatAttachment>();
    for (const t of tools) {
      const r = obj(toolResults.get(t.id)?.result);
      const creativeId = str(r?.creativeId);
      if (!r || !creativeId || r.status !== "script_review") continue;
      if (cards.some((c) => obj(c.att.data)?.creativeId === creativeId || c.att.id === creativeId)) continue;
      byCreative.set(creativeId, { kind: "script_review", id: creativeId, data: { ...r, brandId: str(obj(t.input)?.brandId) ?? ctx.brandId } });
    }
    return [...byCreative.values()];
  }, [tools, toolResults, cards, ctx.brandId]);
  const textHasTable = texts.some((t) => /^\s*\|.+\|\s*$/m.test(t.text));
  return (
    <div id={`row-${row.messageId ?? row.key}`} className="group/turn flex flex-col">
      {trace.length > 0 && <Details parts={trace} toolView={toolView} live={live} toolCount={tools.length} />}
      {visibleTexts.map((t, i) => (
        <Markdown key={t.key} text={t.text} className={cn("text-fg/95", compact ? "text-[14.5px]" : "text-[15.5px] leading-[1.7]", i > 0 && "mt-3")} trailing={streamingText && i === visibleTexts.length - 1 ? <Caret /> : undefined} />
      ))}
      {media.length > 0 && <MediaGrid items={media.map((m) => m.att)} className="mt-3" />}
      {cards.map((c) =>
        c.att.kind === "question" ? (
          <QuestionCard key={c.key} data={obj(c.att.data)} status={questionStatus(c.att)} compact={compact} brandId={ctx.brandId} onSubmit={(a, s, cancelled) => onAnswer(c.att, a, s, cancelled)} />
        ) : (
          <Fragment key={c.key}>
            <AttachmentCard att={c.att} ctx={ctx} />
          </Fragment>
        ),
      )}
      {!live &&
        scriptReviews.map((a) => <ScriptReviewCard key={a.id} att={a} ctx={ctx} showTable={!textHasTable} />)}
      {!live && finalText && <TurnActions text={finalText.text} messageId={row.messageId} createdAt={row.createdAt} initial={row.feedback ?? null} />}
    </div>
  );
});

function Details({ parts, toolView, live, toolCount }: { parts: TurnPart[]; toolView: (t: Extract<TurnPart, { type: "tool" }>) => ToolCallView; live: boolean; toolCount: number }) {
  const [open, setOpen] = useState(false);
  // Group consecutive tool calls into one chip row.
  const groups = useMemo(() => {
    const out: ({ type: "text"; text: string; key: string } | { type: "tools"; calls: Extract<TurnPart, { type: "tool" }>[]; key: string })[] = [];
    for (const p of parts) {
      if (p.type === "tool") {
        const last = out[out.length - 1];
        if (last && last.type === "tools") last.calls.push(p);
        else out.push({ type: "tools", calls: [p], key: p.key });
      } else if (p.type === "text") out.push({ type: "text", text: p.text, key: p.key });
    }
    return out;
  }, [parts]);
  return (
    <div className="mb-4 border-b border-line pb-2.5">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1.5 text-[14px] text-muted transition-colors hover:text-fg">
        Details
        <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
        {!open && toolCount > 0 && <span className="text-[12px] text-subtle">· {toolCount} step{toolCount === 1 ? "" : "s"}{live ? " so far" : ""}</span>}
      </button>
      {open && (
        <div className="mt-2.5 flex flex-col gap-1 border-l border-line pl-3.5 animate-fade-in">
          {groups.map((g) => (g.type === "tools" ? <ToolChipGroup key={g.key} calls={g.calls.map(toolView)} className="my-1" /> : <Markdown key={g.key} text={g.text} className="my-1 text-[13.5px] text-muted" />))}
        </div>
      )}
    </div>
  );
}

function TurnActions({ text, messageId, createdAt, initial }: { text: string; messageId?: string; createdAt?: string; initial: "up" | "down" | null }) {
  const [copied, setCopied] = useState(false);
  const [fb, setFb] = useMessageFeedback(messageId ?? "none");
  const value = fb ?? initial;
  const rate = async (r: "up" | "down") => {
    if (!messageId) return;
    const next = value === r ? null : r;
    setFb(next);
    if (!next) return;
    try {
      await sendMessageFeedback(messageId, next);
      toast({ tone: "success", title: next === "up" ? "Thanks — glad that helped" : "Thanks — Mimic will learn from this", duration: 2500 });
    } catch (e) {
      toast({ tone: "warning", title: "Feedback not saved", body: errorMessage(e) });
    }
  };
  const btn = "grid size-7 place-items-center rounded-md text-subtle transition-colors hover:bg-hover hover:text-fg";
  return (
    <div className="mt-3 flex items-center gap-0.5">
      <ActionTip label={copied ? "Copied" : "Copy"}>
        <button
          type="button"
          className={btn}
          aria-label="Copy"
          onClick={() =>
            void copyText(markdownToPlain(text)).then((ok) => {
              if (!ok) return;
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1400);
            })
          }
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </ActionTip>
      {messageId && (
        <>
          <ActionTip label="Good response">
            <button type="button" className={cn(btn, value === "up" && "text-fg")} aria-label="Good response" aria-pressed={value === "up"} onClick={() => void rate("up")}>
              <ThumbsUp className={cn("size-3.5", value === "up" && "fill-current")} />
            </button>
          </ActionTip>
          <ActionTip label="Bad response">
            <button type="button" className={cn(btn, value === "down" && "text-fg")} aria-label="Bad response" aria-pressed={value === "down"} onClick={() => void rate("down")}>
              <ThumbsDown className={cn("size-3.5", value === "down" && "fill-current")} />
            </button>
          </ActionTip>
        </>
      )}
      {createdAt && <span className="ml-2 text-[12px] text-subtle">{timeLabel(createdAt)}</span>}
    </div>
  );
}

function ActionTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip content={label} side="bottom">
      {children}
    </Tooltip>
  );
}
